/**
 * component-variant-picker-modal.js
 * Modale de selection de variantes — iteration 2026-05-07 Batch 1 (singleton + fix ATC)
 *
 * Custom element : <edj-variant-picker-modal>
 *
 * Comportement :
 *   - Singleton global : 1 seule instance dans le DOM, lazy-créée si absente.
 *   - Ecoute les clics sur [data-edj-card-atc] (boutons "+ Ajouter au panier" sur les
 *     cards produit universelles).
 *   - Si data-has-variants="false" -> ATC direct via /cart/add.js avec data-variant-id.
 *   - Si data-has-variants="true"  -> fetch /products/{handle}.js, ouvre la modale
 *     pour selectionner les options, valide, ATC.
 *   - Les options "Couleur" / "Color" sont rendues sous forme de swatches ronds
 *     (cohérence avec la page produit).
 *   - Au succes : ferme la modale, dispatch `cart:added` (event écouté par <edj-cart-drawer>
 *     qui rafraîchit + ouvre le drawer). FIX 2026-05-07 : auparavant on dispatchait
 *     `cart:open` qui n'avait aucun listener → le drawer ne s'ouvrait jamais.
 *
 * API publique d'ouverture (pour Batch 2+ : cart drawer recos, page panier recos) :
 *   - document.dispatchEvent(new CustomEvent('edj:variant-modal:open', {
 *       detail: { handle: '<product-handle>', triggerButton: <element optionnel> }
 *     }))
 *   - ou : document.querySelector('edj-variant-picker-modal').open(handle, triggerButton)
 *
 * Pas de framework, vanilla JS, style EDJ.
 * Toute injection HTML dynamique passe par escapeHtml(). Pas de contenu utilisateur direct.
 */
(function () {
  'use strict';

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' on ' + url);
      return r.json();
    });
  }

  function escapeHtml(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    var n = parseInt(cents, 10) || 0;
    var euros = n / 100;
    if (Math.round(euros) === euros) {
      return euros.toLocaleString('fr-FR') + ' €';
    }
    return euros.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  /**
   * Mapping couleur (libellé / handle) → hex CSS.
   * Doit rester synchronisé avec snippets/color-swatch-mapping.liquid (côté serveur).
   * Le matcher est case-insensitive, normalise les accents et le handle.
   * Les clés sont des handles (post-normalisation NFD + lowercase + non-alphanum→-).
   * Iter 2026-05-08 Part A — alignement total avec mapping Liquid (couleurs réelles
   * catalogue : bleu-marine, bleu-fonce, gris-clair, gris-fonce, lin, brique, camel,
   * cuivre, noire, mix). Multi-segment fallback fait via colorToHex.
   */
  const COLOR_MAP = {
    sakura: '#E8A8A8', rose: '#E8A8A8', pink: '#E8A8A8',
    indigo: '#3a4a62', bleu: '#3a4a62', blue: '#3a4a62',
    sumi: '#1a1a1a', noir: '#1a1a1a', black: '#1a1a1a', noire: '#1a1a1a',
    blanc: '#f8f4ed', white: '#f8f4ed', ivoire: '#f8f4ed', ivory: '#f8f4ed',
    vert: '#5a7a5a', green: '#5a7a5a', kaki: '#5a7a5a',
    jaune: '#e8c468', yellow: '#e8c468', ocre: '#e8c468',
    rouge: '#b04a2a', red: '#b04a2a', vermillon: '#b04a2a',
    gris: '#808080', grey: '#808080', gray: '#808080',
    'gris-clair': '#b8b8b8', 'light-gray': '#b8b8b8', lightgrey: '#b8b8b8',
    'gris-fonce': '#4a4a4a', 'dark-gray': '#4a4a4a', darkgrey: '#4a4a4a',
    marron: '#6b4423', brown: '#6b4423',
    beige: '#d4b896',
    orange: '#e88848',
    sumire: '#8a6fa8', violet: '#8a6fa8', purple: '#8a6fa8', mauve: '#8a6fa8', lavande: '#8a6fa8', lavender: '#8a6fa8',
    kohaku: '#c8924a', amber: '#c8924a', ambre: '#c8924a', caramel: '#c8924a', 'caramel-clair': '#c8924a',
    asagi: '#7ba6c4', 'bleu-ciel': '#7ba6c4', 'bleu-clair': '#7ba6c4', sky: '#7ba6c4', 'sky-blue': '#7ba6c4',
    'bleu-fonce': '#1e3a5f', 'bleu-nuit': '#1e3a5f', 'navy-blue': '#1e3a5f', 'bleu-marine': '#1e3a5f',
    kuro: '#0d0d0d', jet: '#0d0d0d',
    shiro: '#f0e8d8', creme: '#f0e8d8', cream: '#f0e8d8', ecru: '#f0e8d8',
    lin: '#e8dcc4', linen: '#e8dcc4',
    midori: '#3d8068', jade: '#3d8068', emeraude: '#3d8068', emerald: '#3d8068',
    aka: '#7d2231', cardinal: '#7d2231', bordeaux: '#7d2231', burgundy: '#7d2231', wine: '#7d2231',
    kin: '#c9a25e', or: '#c9a25e', gold: '#c9a25e', dore: '#c9a25e',
    gin: '#b8b8b8', argent: '#b8b8b8', silver: '#b8b8b8', argente: '#b8b8b8',
    cuivre: '#b87333', copper: '#b87333',
    momo: '#e8a888', peche: '#e8a888', peach: '#e8a888', corail: '#e8a888', coral: '#e8a888', saumon: '#e8a888', salmon: '#e8a888',
    ao: '#1e3a5f', aoi: '#1e3a5f', cobalt: '#1e3a5f', navy: '#1e3a5f', marine: '#1e3a5f',
    cha: '#9a8467', chai: '#9a8467', tan: '#9a8467', taupe: '#9a8467', sable: '#9a8467', sand: '#9a8467',
    camel: '#c8924a',
    midnight: '#2a2a2a', minuit: '#2a2a2a', anthracite: '#2a2a2a', charbon: '#2a2a2a', charcoal: '#2a2a2a',
    turquoise: '#3a8b8b', teal: '#3a8b8b', cyan: '#3a8b8b',
    blush: '#f0c8c0', poudre: '#f0c8c0', powder: '#f0c8c0', 'rose-pale': '#f0c8c0', 'pale-pink': '#f0c8c0',
    fuchsia: '#c8408a', magenta: '#c8408a', 'rose-vif': '#c8408a',
    olive: '#6a7048', 'vert-olive': '#6a7048', 'kaki-fonce': '#6a7048',
    moutarde: '#c89640', mustard: '#c89640', curry: '#c89640',
    terracotta: '#b04a2a', 'terre-cuite': '#b04a2a', rouille: '#b04a2a', rust: '#b04a2a', brique: '#b04a2a', brick: '#b04a2a',
    pourpre: '#5a2848', plum: '#5a2848', prune: '#5a2848', eggplant: '#5a2848',
    mix: 'linear-gradient(135deg, #e8a8a8 0%, #3a4a62 33%, #5a7a5a 66%, #e8c468 100%)',
    multicolore: 'linear-gradient(135deg, #e8a8a8 0%, #3a4a62 33%, #5a7a5a 66%, #e8c468 100%)',
    multicolor: 'linear-gradient(135deg, #e8a8a8 0%, #3a4a62 33%, #5a7a5a 66%, #e8c468 100%)',
    matcha: '#7a8a5c',
    akai: '#b94a3a',
    cafe: '#6b4423', coffee: '#6b4423', moka: '#6b4423', mocha: '#6b4423',
    chocolat: '#4a2c1a', chocolate: '#4a2c1a',
    bois: '#9a7050', wood: '#9a7050',
    naturel: '#d4b896', natural: '#d4b896', nature: '#d4b896',
    sauge: '#8a9b7c', sage: '#8a9b7c',
    menthe: '#9bc1a8', mint: '#9bc1a8'
  };

  function normalizeColorKey(value) {
    if (!value) return '';
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function colorToHex(value) {
    const key = normalizeColorKey(value);
    if (!key) return null;
    // 1. Match exact full handle (priorité maximale — couvre "bleu-marine",
    //    "bleu-fonce", "gris-clair", etc.)
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    // 2. Multi-segment fallback : on essaye chaque paire/triplet contigus
    //    (longest match first), puis chaque segment seul.
    const parts = key.split('-');
    // Triplets adjacents (rare mais robuste)
    for (let i = 0; i < parts.length - 2; i++) {
      const tri = parts.slice(i, i + 3).join('-');
      if (COLOR_MAP[tri]) return COLOR_MAP[tri];
    }
    // Paires adjacentes
    for (let i = 0; i < parts.length - 1; i++) {
      const pair = parts.slice(i, i + 2).join('-');
      if (COLOR_MAP[pair]) return COLOR_MAP[pair];
    }
    // Segments seuls — essayer les plus "spécifiques" d'abord (ceux qui ne sont
    // pas dans la liste des modificateurs génériques).
    const generic = new Set(['clair', 'fonce', 'pale', 'vif', 'sac', 'fleurs',
      'aux', 'and', 'et', 'plus', 'avec', 'soupe']);
    const ranked = [...parts].filter(p => !generic.has(p)).concat(
      parts.filter(p => generic.has(p))
    );
    for (const p of ranked) {
      if (COLOR_MAP[p]) return COLOR_MAP[p];
    }
    return null;
  }

  function isColorOption(name) {
    if (!name) return false;
    return /^(color|colour|couleur|teinte|coloris)$/i.test(String(name).trim());
  }

  let modalInstance = null;

  function getOrCreateModal() {
    if (modalInstance && document.body.contains(modalInstance)) return modalInstance;
    let el = document.querySelector('edj-variant-picker-modal');
    if (!el) {
      el = document.createElement('edj-variant-picker-modal');
      document.body.appendChild(el);
    }
    modalInstance = el;
    return el;
  }

  function addToCartDirect(variantId, button) {
    if (!variantId) {
      const handle = button && button.getAttribute('data-product-handle');
      if (handle) window.location.href = '/products/' + handle;
      return;
    }
    if (button) button.classList.add('is-loading');

    return fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    }).then(function (resp) {
      if (button) {
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        setTimeout(function () { button.classList.remove('is-success'); }, 1500);
      }
      // FIX 2026-05-07 : `cart:added` est l'event écouté par <edj-cart-drawer>.
      // Auparavant on dispatchait `cart:open` qui n'avait aucun listener.
      dispatch('cart:added', { source: 'edj-product-card', response: resp });
    }).catch(function (err) {
      if (button) button.classList.remove('is-loading');
      console.warn('[edj-variant-picker-modal] add to cart failed', err);
      const handle = button && button.getAttribute('data-product-handle');
      if (handle) window.location.href = '/products/' + handle;
    });
  }

  class EdjVariantPickerModal extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._product = null;
      this._selected = {};
      this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    connectedCallback() {
      this.classList.add('edj-vpm');
      this.setAttribute('aria-hidden', 'true');
      if (!this.innerHTML.trim()) this._renderShell();
      this.addEventListener('click', (e) => {
        if (e.target === this || e.target.matches('[data-edj-vpm-close]')) {
          this.close();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._handleKeyDown);
    }

    _renderShell() {
      // Static shell, only safe text. No untrusted input.
      const shell = document.createElement('div');
      shell.className = 'edj-vpm__panel';
      shell.setAttribute('role', 'dialog');
      shell.setAttribute('aria-modal', 'true');
      shell.setAttribute('aria-labelledby', 'edj-vpm-title');

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'edj-vpm__close';
      closeBtn.setAttribute('data-edj-vpm-close', '');
      closeBtn.setAttribute('aria-label', 'Fermer');
      closeBtn.textContent = '×';

      const content = document.createElement('div');
      content.className = 'edj-vpm__content';
      content.setAttribute('data-edj-vpm-content', '');

      const loading = document.createElement('div');
      loading.className = 'edj-vpm__loading';
      loading.textContent = 'Chargement…';
      content.appendChild(loading);

      shell.appendChild(closeBtn);
      shell.appendChild(content);
      this.replaceChildren(shell);
    }

    _handleKeyDown(e) {
      if (e.key === 'Escape' && this._open) this.close();
    }

    open(handle, triggerButton) {
      this._open = true;
      this._triggerButton = triggerButton;
      this._handle = handle;
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('edj-vpm-open');
      document.addEventListener('keydown', this._handleKeyDown);

      const content = this.querySelector('[data-edj-vpm-content]');
      content.replaceChildren();
      const loading = document.createElement('div');
      loading.className = 'edj-vpm__loading';
      loading.textContent = 'Chargement…';
      content.appendChild(loading);

      fetchJSON('/products/' + encodeURIComponent(handle) + '.js')
        .then((product) => {
          this._product = product;
          this._selected = {};
          const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
          if (firstAvailable && firstAvailable.options) {
            firstAvailable.options.forEach((val, i) => { this._selected[i] = val; });
          }
          this._renderProduct();
        })
        .catch((err) => {
          console.warn('[edj-variant-picker-modal] fetch product failed', err);
          content.replaceChildren();
          const errEl = document.createElement('div');
          errEl.className = 'edj-vpm__error';
          errEl.textContent = 'Impossible de charger les variantes. ';
          const link = document.createElement('a');
          link.href = '/products/' + encodeURIComponent(handle);
          link.textContent = 'Voir la page produit →';
          errEl.appendChild(link);
          content.appendChild(errEl);
        });
    }

    close() {
      this._open = false;
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('edj-vpm-open');
      document.removeEventListener('keydown', this._handleKeyDown);
      if (this._triggerButton) {
        this._triggerButton.classList.remove('is-loading');
        try { this._triggerButton.focus(); } catch (e) {}
      }
    }

    _renderProduct() {
      const p = this._product;
      const content = this.querySelector('[data-edj-vpm-content]');
      const matched = this._matchVariant();
      const priceCents = matched ? matched.price : p.price;
      const compareCents = matched ? matched.compare_at_price : p.compare_at_price;

      content.replaceChildren();
      const wrap = document.createElement('div');
      wrap.className = 'edj-vpm__product';

      // Media
      const media = document.createElement('div');
      media.className = 'edj-vpm__media';
      if (p.featured_image) {
        const img = document.createElement('img');
        img.src = p.featured_image;
        img.alt = p.title || '';
        img.className = 'edj-vpm__image';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      wrap.appendChild(media);

      // Body
      const body = document.createElement('div');
      body.className = 'edj-vpm__body';

      const title = document.createElement('h2');
      title.id = 'edj-vpm-title';
      title.className = 'edj-vpm__title';
      title.textContent = p.title || '';
      body.appendChild(title);

      const priceEl = document.createElement('div');
      priceEl.className = 'edj-vpm__price';
      priceEl.textContent = formatMoney(priceCents);
      if (compareCents && compareCents > priceCents) {
        const cmp = document.createElement('span');
        cmp.className = 'edj-vpm__compare';
        cmp.textContent = ' ' + formatMoney(compareCents);
        priceEl.appendChild(cmp);
      }
      body.appendChild(priceEl);

      // Options
      const optsContainer = document.createElement('div');
      optsContainer.className = 'edj-vpm__options';
      optsContainer.setAttribute('data-edj-vpm-options', '');

      (p.options || []).forEach((opt, i) => {
        const optName = typeof opt === 'string' ? opt : (opt.name || '');
        const values = this._getOptionValues(i);
        const selected = this._selected[i];
        const isColor = isColorOption(optName);

        const optWrap = document.createElement('div');
        optWrap.className = 'edj-vpm__option' + (isColor ? ' edj-vpm__option--color' : '');

        const labelEl = document.createElement('div');
        labelEl.className = 'edj-vpm__option-label';
        labelEl.textContent = optName + ' : ';
        const labelVal = document.createElement('span');
        labelVal.className = 'edj-vpm__option-selected';
        labelVal.textContent = selected || '-';
        labelEl.appendChild(labelVal);
        optWrap.appendChild(labelEl);

        const valuesEl = document.createElement('div');
        valuesEl.className = 'edj-vpm__option-values' + (isColor ? ' edj-vpm__option-values--color' : '');
        values.forEach((v) => {
          const isAvail = this._isOptionValueAvailable(i, v);
          const isSelected = selected === v;
          const btn = document.createElement('button');
          btn.type = 'button';
          if (isColor) {
            const hex = colorToHex(v);
            btn.className = 'edj-vpm__color-swatch'
              + (isSelected ? ' is-selected' : '')
              + (isAvail ? '' : ' is-unavailable');
            btn.setAttribute('aria-label', v);
            btn.title = v;
            if (hex) {
              btn.style.background = hex;
            } else {
              btn.classList.add('edj-vpm__color-swatch--noref');
              btn.textContent = v.charAt(0).toUpperCase();
            }
          } else {
            btn.className = 'edj-vpm__opt-btn'
              + (isSelected ? ' is-selected' : '')
              + (isAvail ? '' : ' is-unavailable');
            btn.textContent = v;
          }
          btn.setAttribute('data-edj-vpm-option-position', String(i));
          btn.setAttribute('data-edj-vpm-option-value', v);
          btn.addEventListener('click', () => {
            this._selected[i] = v;
            this._renderProduct();
          });
          valuesEl.appendChild(btn);
        });
        optWrap.appendChild(valuesEl);
        optsContainer.appendChild(optWrap);
      });
      body.appendChild(optsContainer);

      // CTA
      const matchAvailable = matched && matched.available;
      const ctaLabel = matchAvailable ? 'Ajouter au panier' : (matched ? 'Variante épuisée' : 'Sélectionnez une option');
      const cta = document.createElement('button');
      cta.type = 'button';
      cta.className = 'edj-vpm__cta';
      cta.setAttribute('data-edj-vpm-add', '');
      cta.textContent = ctaLabel;
      if (!matchAvailable) cta.disabled = true;
      cta.addEventListener('click', () => this._handleAdd(cta));
      body.appendChild(cta);

      // Lien vers la page produit
      const viewLink = document.createElement('a');
      viewLink.href = '/products/' + encodeURIComponent(p.handle || '');
      viewLink.className = 'edj-vpm__view-link';
      viewLink.textContent = 'Voir la fiche produit complète →';
      body.appendChild(viewLink);

      wrap.appendChild(body);
      content.appendChild(wrap);
    }

    _getOptionValues(position) {
      const seen = new Set();
      const values = [];
      this._product.variants.forEach((v) => {
        const val = (v.options || [])[position];
        if (val && !seen.has(val)) { seen.add(val); values.push(val); }
      });
      return values;
    }

    _isOptionValueAvailable(position, value) {
      return this._product.variants.some((v) => {
        if (!v.available) return false;
        const opts = v.options || [];
        if (opts[position] !== value) return false;
        return Object.keys(this._selected).every((kStr) => {
          const k = parseInt(kStr, 10);
          if (k === position) return true;
          if (this._selected[k] === undefined) return true;
          return opts[k] === this._selected[k];
        });
      });
    }

    _matchVariant() {
      if (!this._product) return null;
      return this._product.variants.find((v) => {
        const opts = v.options || [];
        return Object.keys(this._selected).every((kStr) => {
          const k = parseInt(kStr, 10);
          return opts[k] === this._selected[k];
        });
      }) || null;
    }

    _handleAdd(button) {
      const matched = this._matchVariant();
      if (!matched || !matched.available) return;
      button.classList.add('is-loading');
      button.disabled = true;
      fetchJSON('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: matched.id, quantity: 1 }] })
      }).then((resp) => {
        button.classList.remove('is-loading');
        button.classList.add('is-success');
        button.textContent = 'Ajouté ✓';
        setTimeout(() => {
          this.close();
          // FIX 2026-05-07 : dispatch `cart:added` (écouté par <edj-cart-drawer>)
          // au lieu de `edj:cart:refresh` + `cart:open` qui n'avaient aucun listener.
          // L'event `cart:added` déclenche refresh + auto-open du drawer.
          dispatch('cart:added', { source: 'edj-variant-picker-modal', response: resp });
        }, 700);
      }).catch((err) => {
        console.warn('[edj-variant-picker-modal] add failed', err);
        button.classList.remove('is-loading');
        button.disabled = false;
        button.textContent = 'Erreur, réessayez';
      });
    }
  }

  customElements.define('edj-variant-picker-modal', EdjVariantPickerModal);

  /**
   * Helper d'ouverture de la modale (singleton) — utilisable depuis n'importe où.
   * @param {string} handle — product handle
   * @param {HTMLElement} [triggerButton] — bouton déclencheur (pour focus return + spinner)
   */
  function openVariantModal(handle, triggerButton) {
    if (!handle) return;
    const modal = getOrCreateModal();
    modal.open(handle, triggerButton || null);
  }

  // Expose API publique (utilisable depuis Liquid inline scripts ou autres composants)
  window.EDJ = window.EDJ || {};
  window.EDJ.variantModal = { open: openVariantModal };

  // Click handler global pour [data-edj-card-atc] (cards produit universelles)
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-edj-card-atc]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const hasVariants = btn.getAttribute('data-has-variants') === 'true';
    const handle = btn.getAttribute('data-product-handle');
    const variantId = btn.getAttribute('data-variant-id');

    if (!hasVariants && variantId) {
      addToCartDirect(variantId, btn);
      return;
    }
    if (hasVariants && handle) {
      openVariantModal(handle, btn);
      return;
    }
    if (handle) window.location.href = '/products/' + handle;
  }, true);

  // Custom event d'ouverture — utilisable par les sources qui ne peuvent pas
  // monter `[data-edj-card-atc]` directement (ex : cart drawer recos, page panier recos).
  // Usage :
  //   document.dispatchEvent(new CustomEvent('edj:variant-modal:open', {
  //     detail: { handle: 'mon-produit', triggerButton: btnElement }
  //   }))
  document.addEventListener('edj:variant-modal:open', function (e) {
    const detail = e.detail || {};
    if (!detail.handle) {
      console.warn('[edj-variant-picker-modal] edj:variant-modal:open missing handle in detail');
      return;
    }
    openVariantModal(detail.handle, detail.triggerButton || null);
  });
})();
